import React, { useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { CheckCircle2, Clock, Coins, ExternalLink, Loader2, LogIn } from "lucide-react";
import {
  CreditProductResponse,
  PaymentOrderResponse,
  PaymentOrderStatus,
  formatCreditAmount,
} from "@packages/global";
import { Modal, StatusPill } from "@packages/ui";
import { useNavigate } from "react-router-dom";
import { paymentsApi } from "../../services/user";
import { useAuthStore } from "../../stores/authStore";
import { useDialog } from "../../hooks/useDialog";

interface CreditPurchaseDialogProps {
  isOpen: boolean;
  products: CreditProductResponse[];
  loading?: boolean;
  selectedProduct?: CreditProductResponse | null;
  onClose: () => void;
  onPaid?: () => Promise<void> | void;
}

const CreditPurchaseDialog: React.FC<CreditPurchaseDialogProps> = ({
  isOpen,
  products,
  loading = false,
  selectedProduct = null,
  onClose,
  onPaid,
}) => {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const { alert, toast } = useDialog();
  const [creatingProductId, setCreatingProductId] = useState<string | null>(null);
  const [activeOrder, setActiveOrder] = useState<PaymentOrderResponse | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const autoCreatedProductIdRef = useRef<string | null>(null);
  const directMode = Boolean(selectedProduct);

  const createOrder = useCallback(async (product: CreditProductResponse) => {
    if (!token) {
      navigate("/login");
      return;
    }
    setCreatingProductId(product.id);
    try {
      const result = await paymentsApi.createOrder({ productId: product.id });
      setActiveOrder(result.order);
    } catch (error: any) {
      autoCreatedProductIdRef.current = null;
      await alert(error?.response?.data?.message ?? "创建支付订单失败", "充值失败", "danger");
    } finally {
      setCreatingProductId(null);
    }
  }, [alert, navigate, token]);

  useEffect(() => {
    if (isOpen) return;
    setActiveOrder(null);
    setQrImageUrl("");
    setCreatingProductId(null);
    autoCreatedProductIdRef.current = null;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedProduct || !token || activeOrder || creatingProductId) return;
    if (autoCreatedProductIdRef.current === selectedProduct.id) return;
    autoCreatedProductIdRef.current = selectedProduct.id;
    void createOrder(selectedProduct);
  }, [activeOrder, createOrder, creatingProductId, isOpen, selectedProduct, token]);

  useEffect(() => {
    if (!activeOrder || activeOrder.status !== PaymentOrderStatus.PENDING) return undefined;
    let paidNotified = false;
    const timer = window.setInterval(async () => {
      try {
        const latest = await paymentsApi.getOrder(activeOrder.id);
        setActiveOrder(latest);
        if (latest.status === PaymentOrderStatus.PAID && !paidNotified) {
          paidNotified = true;
          toast("充值成功，积分已到账", "success");
          await onPaid?.();
        }
      } catch {
        // Keep polling during temporary network hiccups.
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [activeOrder, onPaid, toast]);

  useEffect(() => {
    let active = true;
    if (!activeOrder?.codeUrl) {
      setQrImageUrl("");
      return () => {
        active = false;
      };
    }
    QRCode.toDataURL(activeOrder.codeUrl, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#111827", light: "#ffffff" },
    })
      .then((url) => {
        if (active) setQrImageUrl(url);
      })
      .catch(() => {
        if (active) setQrImageUrl("");
      });
    return () => {
      active = false;
    };
  }, [activeOrder?.codeUrl]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="充值积分"
      description={directMode ? "正在为所选套餐生成微信支付二维码，支付成功后积分自动到账。" : "选择套餐后使用微信支付，支付成功后积分自动到账。"}
      showCloseButton
      maxWidthClassName={directMode ? "max-w-3xl" : "max-w-5xl"}
    >
      <div className={`grid gap-5 ${directMode ? "lg:grid-cols-[minmax(0,1fr)_300px]" : "lg:grid-cols-[minmax(0,1fr)_280px]"}`}>
        {directMode && selectedProduct ? (
          <div className="rounded-[12px] border border-border-subtle bg-bg-surface p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate text-[18px] font-semibold text-text-primary">{selectedProduct.name}</h3>
                <p className="mt-2 text-[13px] leading-6 text-text-muted">{selectedProduct.description}</p>
              </div>
              {selectedProduct.badge && <StatusPill tone="success">{selectedProduct.badge}</StatusPill>}
            </div>
            <div className="mt-7">
              <div className="flex items-end gap-2">
                <span className="font-mono text-[34px] font-semibold leading-none text-text-primary">{formatCreditAmount(selectedProduct.credits)}</span>
                <span className="pb-1 text-[12px] text-text-muted">积分</span>
              </div>
              <div className="mt-2 text-[12px] text-text-muted">有效期 {selectedProduct.validityDays} 天</div>
            </div>
            <div className="mt-8 rounded-[10px] border border-border-subtle bg-bg-elevated p-4">
              <div className="text-[11px] text-text-muted">支付金额</div>
              <div className="mt-1 text-[28px] font-semibold text-primary-ink">¥{(selectedProduct.amountCents / 100).toFixed(2)}</div>
              <p className="mt-3 text-[12px] leading-5 text-text-secondary">
                {token ? "请使用微信扫码完成支付，支付完成后系统会自动确认并发放积分。" : "登录后即可在网页端生成支付二维码并完成购买。"}
              </p>
            </div>
            {!token && (
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-[9px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover"
              >
                <LogIn className="h-4 w-4" />
                登录购买
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {loading ? (
              <div className="rounded-[12px] border border-border-subtle bg-bg-surface p-5 text-[13px] text-text-muted md:col-span-3">
                正在加载套餐...
              </div>
            ) : products.length === 0 ? (
              <div className="rounded-[12px] border border-border-subtle bg-bg-surface p-5 text-[13px] text-text-muted md:col-span-3">
                暂无可购买套餐。
              </div>
            ) : (
              products.map((product) => (
                <article key={product.id} className="flex min-h-[210px] flex-col rounded-[12px] border border-border-subtle bg-bg-surface p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-[16px] font-semibold text-text-primary">{product.name}</h3>
                      <p className="mt-1 line-clamp-2 text-[12.5px] leading-5 text-text-muted">{product.description}</p>
                    </div>
                    {product.badge && <StatusPill tone="success">{product.badge}</StatusPill>}
                  </div>
                  <div className="mt-5">
                    <div className="font-mono text-[28px] font-semibold text-text-primary">{formatCreditAmount(product.credits)}</div>
                    <div className="mt-1 text-[12px] text-text-muted">有效期 {product.validityDays} 天</div>
                  </div>
                  <div className="mt-auto flex items-end justify-between gap-3 pt-5">
                    <div>
                      <div className="text-[11px] text-text-muted">支付金额</div>
                      <div className="text-[20px] font-semibold text-primary-ink">¥{(product.amountCents / 100).toFixed(2)}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void createOrder(product)}
                      disabled={creatingProductId === product.id}
                      className="inline-flex min-h-9 items-center gap-2 rounded-[9px] bg-action-primary px-4 text-[13px] font-semibold text-white hover:bg-action-primary-hover disabled:opacity-50"
                    >
                      {creatingProductId === product.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                      {token ? (creatingProductId === product.id ? "下单中" : "购买") : "登录购买"}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        <aside className="rounded-[12px] border border-border-subtle bg-bg-elevated p-5">
          {activeOrder ? (
            <div>
              <div className="grid h-[236px] place-items-center rounded-[12px] border border-border-subtle bg-white p-2">
                {activeOrder.status === PaymentOrderStatus.PAID ? (
                  <CheckCircle2 className="h-16 w-16 text-success" />
                ) : qrImageUrl ? (
                  <img src={qrImageUrl} alt="微信支付二维码" className="h-[220px] w-[220px]" />
                ) : (
                  <Clock className="h-12 w-12 text-text-muted" />
                )}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <h3 className="text-[15px] font-semibold text-text-primary">微信支付订单</h3>
                <StatusPill tone={activeOrder.status === PaymentOrderStatus.PAID ? "success" : "warning"}>
                  {activeOrder.status === PaymentOrderStatus.PAID ? "已支付" : "待支付"}
                </StatusPill>
              </div>
              <div className="mt-3 space-y-2 text-[12px] text-text-secondary">
                <div>订单号：<span className="font-mono">{activeOrder.outTradeNo}</span></div>
                <div>积分：{formatCreditAmount(activeOrder.credits)}</div>
                <div>金额：¥{(activeOrder.amountCents / 100).toFixed(2)}</div>
                <div>过期时间：{formatDateTime(activeOrder.expiresAt)}</div>
              </div>
              {activeOrder.codeUrl && activeOrder.status === PaymentOrderStatus.PENDING && (
                <a
                  href={activeOrder.codeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex min-h-9 items-center gap-2 rounded-[9px] border border-border-default bg-bg-surface px-4 text-[13px] font-semibold text-text-secondary hover:bg-bg-elevated"
                >
                  <ExternalLink className="h-4 w-4" />
                  打开支付链接
                </a>
              )}
            </div>
          ) : (
            <div className="flex min-h-[236px] flex-col justify-center text-[13px] leading-6 text-text-secondary">
              {creatingProductId ? (
                <>
                  <Loader2 className="mb-3 h-8 w-8 animate-spin text-primary-ink" />
                  <div className="font-semibold text-text-primary">正在生成支付二维码</div>
                  <p className="mt-2">订单创建完成后，微信支付二维码会自动显示在这里。</p>
                </>
              ) : !token && directMode ? (
                <>
                  <LogIn className="mb-3 h-8 w-8 text-primary-ink" />
                  <div className="font-semibold text-text-primary">登录后继续购买</div>
                  <p className="mt-2">支付订单需要绑定到你的账号，登录后可以在网页端直接扫码购买。</p>
                </>
              ) : (
                <>
                  <Coins className="mb-3 h-8 w-8 text-primary-ink" />
                  <div className="font-semibold text-text-primary">{directMode ? "等待生成支付码" : "选择套餐开始充值"}</div>
                  <p className="mt-2">支付二维码会在这里显示。请使用微信扫码支付，到账后余额会自动刷新。</p>
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    </Modal>
  );
};

function formatDateTime(value: string | Date) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

export default CreditPurchaseDialog;
